import { z } from "zod"

import {
  auditLogEntrySchema,
  tenantStatusSchema,
  vertragsmodellSchema,
} from "./responses"
import { trainingsEinheitSchema } from "./schemas"

/**
 * Contracts of the admin area.
 *
 * Every figure here is an aggregate. No schema in this file has a field for a
 * user ID, name or email: the serializer strips anything not declared, so a
 * query that accidentally selects personal data still cannot deliver it.
 */

export const tenantSlugSchema = z
  .string()
  .regex(
    /^[a-z0-9]+(-[a-z0-9]+)*$/,
    "Nur Kleinbuchstaben, Ziffern und Bindestriche.",
  )
  .min(2)
  .max(48)

export const createTenantSchema = z.object({
  slug: tenantSlugSchema,
  name: z.string().trim().min(2),
  region: z.string().trim().default(""),
  status: tenantStatusSchema.default("active"),
  licenseSeats: z.number().int().min(0).default(0),
})
export type CreateTenantInput = z.infer<typeof createTenantSchema>

export const updateTenantSchema = z.object({
  name: z.string().trim().min(2).optional(),
  region: z.string().trim().optional(),
  status: tenantStatusSchema.optional(),
  licenseSeats: z.number().int().min(0).optional(),
  vertragsmodell: vertragsmodellSchema.optional(),
  monthlyFixedPriceEur: z.number().min(0).optional(),
  perUserPriceEur: z.number().min(0).optional(),
  /** ISO date; an empty string clears it. */
  vertragBeginn: z.union([z.iso.datetime({ offset: true }), z.literal("")]).optional(),
  vertragEnde: z.union([z.iso.datetime({ offset: true }), z.literal("")]).optional(),
  abrechnungsKontakt: z.string().trim().optional(),
})
export type UpdateTenantInput = z.infer<typeof updateTenantSchema>

/** ISO timestamp of a group's term; an empty string clears it. */
const gueltigkeitSchema = z
  .union([z.iso.datetime({ offset: true }), z.literal("")])
  .optional()

export const upsertTeamSchema = z.object({
  name: z.string().trim().min(1),
  lead: z.string().trim().default(""),
  focus: z.string().trim().default(""),
  pilot: z.boolean().default(false),
  status: tenantStatusSchema.default("active"),
  gueltigAb: gueltigkeitSchema,
  gueltigBis: gueltigkeitSchema,
})
export type UpsertTeamInput = z.infer<typeof upsertTeamSchema>

// Not `upsertTeamSchema.partial()`: Zod 4 still fills in the defaults, so
// renaming a paused group would quietly reactivate it.
export const updateTeamSchema = z.object({
  name: z.string().trim().min(1).optional(),
  lead: z.string().trim().optional(),
  focus: z.string().trim().optional(),
  pilot: z.boolean().optional(),
  status: tenantStatusSchema.optional(),
  gueltigAb: gueltigkeitSchema,
  gueltigBis: gueltigkeitSchema,
})
export type UpdateTeamInput = z.infer<typeof updateTeamSchema>

/** Moves accounts into or out of a group. Adding someone takes them out of their previous group. */
export const setTeamMembersSchema = z.object({
  add: z.array(z.string()).default([]),
  remove: z.array(z.string()).default([]),
})
export type SetTeamMembersInput = z.infer<typeof setTeamMembersSchema>

export const auditLogPageSchema = z.object({
  entries: z.array(auditLogEntrySchema),
  /** Pass as `cursor` for the next page; absent on the last one. */
  nextCursor: z.string().optional(),
})
export type AuditLogPage = z.infer<typeof auditLogPageSchema>

/**
 * Below this many people a group yields no figures. With three people, a
 * pass rate is close to a statement about each of them.
 */
export const MINDEST_GRUPPENGROESSE = 5

/** A figure that is withheld when the group is too small. */
const gedeckelt = <T extends z.ZodTypeAny>(wert: T) =>
  z.discriminatedUnion("verfuegbar", [
    z.object({ verfuegbar: z.literal(true), wert }),
    z.object({ verfuegbar: z.literal(false), grund: z.literal("gruppe_zu_klein") }),
  ])

export const pruefungsAuswertungGruppeSchema = z.object({
  tenantId: z.string(),
  teamId: z.string().optional(),
  teilnehmende: z.number().int(),
  gesamt: gedeckelt(
    z.object({ antworten: z.number().int(), quoteRichtig: z.number() }),
  ),
  lernfelder: z.array(
    z.object({
      lernfeldId: z.string(),
      titel: z.string(),
      auswertung: gedeckelt(
        z.object({
          teilnehmende: z.number().int(),
          antworten: z.number().int(),
          quoteRichtig: z.number(),
        }),
      ),
    }),
  ),
})
export type PruefungsAuswertungGruppe = z.infer<
  typeof pruefungsAuswertungGruppeSchema
>

export const bearbeitungsstatusSchema = z.object({
  tenantId: z.string(),
  teamId: z.string().optional(),
  /** Advisors of the group who have signed in and are not locked. */
  mitglieder: z.number().int(),
  einheiten: z.array(
    z.object({
      einheit: trainingsEinheitSchema,
      status: gedeckelt(
        z.object({
          begonnen: z.number().int(),
          abgeschlossen: z.number().int(),
        }),
      ),
    }),
  ),
})
export type Bearbeitungsstatus = z.infer<typeof bearbeitungsstatusSchema>

/**
 * The caller's CASL rules, for the interface only. The server checks every
 * request again, so a tampered rule set opens nothing.
 */
export const abilityRuleSchema = z.object({
  action: z.union([z.string(), z.array(z.string())]),
  subject: z.union([z.string(), z.array(z.string())]),
  conditions: z.record(z.string(), z.unknown()).optional(),
  inverted: z.boolean().optional(),
})
export const abilityResponseSchema = z.object({
  rules: z.array(abilityRuleSchema),
})
export type AbilityResponse = z.infer<typeof abilityResponseSchema>
